import React, { useCallback, useEffect, useRef, useState } from 'react';
import EventContainer from './events/event-container';
import EventListContainer from './events/events-list-container';
import FullMonthView from './calendars-view/fullMonthView';
import WeekdayHeader from './headers/weekDayHeader';
import MonthSelector from './selectors/monthSelector';
import CalendarHeader from './headers/calendarHeader';
import FilterScreen from './filters/filterScreen';
import { getProperties, nameof } from 'utils/object';
import { EventDto, EventsStore } from 'stores/events/events-store';
import { OrderDefinition, Query } from 'stores/dataStore';
import { connect } from 'redux-scaffolding-ts';
import MobileListSpinner from 'site/pages/shared/events-and-requests/mobile-list-spinner';
// import MobileListFooter from 'site/pages/shared/events-and-requests/mobile-list-footer';
import { useTranslation } from 'react-i18next';
import { RequestDto, RequestsStore } from 'stores/requests/requests-store';
import { IdentityService } from 'services/identity-service';
import { container } from 'inversify.config';
import { getLocationDefaultRequestFilterValue } from '../shared-scheduler-components/scheduler-methods';
import RequestContainer from './requests/request-container';
import { buildRequestFormViewModel } from 'stores/requests/request-form-store';
import MobileRequestsList from 'site/pages/requests/mobile-requests-list';
import { UserDto } from 'stores/users/users-store';
import { LocationDto } from 'stores/configuration/locations/locations-store';

interface MobileSchedulerPageProps {
  eventsStore?: EventsStore;
  requestsStore?: RequestsStore;
}

const MobileSchedulerPage: React.FC<MobileSchedulerPageProps> = ({ eventsStore, requestsStore }) => {
  const { t } = useTranslation();
  const ITEMS_PER_PAGE = 100000;
  let defaultOrder: OrderDefinition[] = [{ direction: 'Descending', field: 'friendlyEventId', useProfile: false }];

  const [currentDate, setCurrentDate] = useState(new Date());
  const [locationList, setLocationList] = useState<LocationDto[]>([]);
  const [instructorsNameList, setInstructorsNameList] = useState<UserDto[]>([]);

  const getFirstDayOfMonth = (date: Date): Date => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  };

  const getLastDayOfMonth = (date: Date): Date => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0);
  };

  const identityService: IdentityService = container.get(IdentityService);
  const [entityType, setEntityType] = useState<'events' | 'requests'>('events');

  const getEventStatusFilters = useCallback(
    (type: 'events' | 'requests', date?: Date) => {
      const dateAux = date || currentDate;
      let auxActiveFilters;

      if (type === 'events') {
        auxActiveFilters = {
          startDate: { le: new Date(getLastDayOfMonth(dateAux)) },
          endDate: { ge: new Date(getFirstDayOfMonth(dateAux)) }
        };
      } else {
        auxActiveFilters = getLocationDefaultRequestFilterValue(
          identityService.activeRole,
          identityService.getUserInfo(),
          false /*isGlobalReporting*/
        );

        const dateFilters = {
          and: [{ startDate: { le: new Date(getLastDayOfMonth(dateAux)) } }, { endDate: { ge: new Date(getFirstDayOfMonth(dateAux)) } }]
        };
        auxActiveFilters.push(dateFilters);
      }

      return auxActiveFilters;
    },
    [currentDate, identityService]
  );

  const handleEntityTypeChange = (type: 'events' | 'requests') => () => {
    const auxFilter = getEventStatusFilters(type);
    setEntityType(type);
    setActiveFilters({
      EventStatus: auxFilter
    });
    setLocationList([]);
    setInstructorsNameList([]);
  };

  const [showFilters, setShowFilters] = useState(false);
  // const [showActionFilters] = useState(false);
  const [selectedEventItem, setSelectedEventItem] = useState<EventDto>(null);
  const [selectedRequestItem, setSelectedRequestItem] = useState<RequestDto>(null);
  const [showFullMonth, setShowFullMonth] = useState(false);
  const [selectedFilterType, setSelectedFilterType] = useState<'instructor' | 'location'>('instructor');
  const [selectedDate, setSelectedDate] = useState<Date>(null);
  const [viewMode, setViewMode] = useState<'week' | 'month'>('week');
  const [error, setError] = useState<string | null>(null);
  // const [setTotalItems] = useState<number>(0);
  const [events, setEvents] = useState<EventDto[]>([]);
  const [requests, setRequests] = useState<RequestDto[]>([]);
  const [listedEvents, setListedEvents] = useState<EventDto[]>([]);
  const [listedRequests, setListedRequests] = useState<RequestDto[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage] = useState<number>(1);
  const [isOnlySupportDetailsEnabled] = useState<boolean>(false);
  const [readOnlyForPocs] = useState<boolean>(true);
  const [paso, setPaso] = useState<boolean>(false);
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: any }>({
    EventStatus: getEventStatusFilters('events')
  });

  const queryRef = useRef<Query>({
    searchQuery: '',
    orderBy: defaultOrder,
    take: ITEMS_PER_PAGE,
    skip: 0
  });

  const handlePrevMonth = () => {
    setCurrentDate(prev => {
      const prevMonth = new Date(prev.getFullYear(), prev.getMonth() - 1, 1);
      const newActiveFilters = {
        ...activeFilters,
        EventStatus: getEventStatusFilters(entityType, prevMonth)
      };
      setActiveFilters(newActiveFilters);
      return prevMonth;
    });
  };

  const handleNextMonth = () => {
    setCurrentDate(prev => {
      const nextMonth = new Date(prev.getFullYear(), prev.getMonth() + 1, 1);
      const newActiveFilters = {
        ...activeFilters,
        EventStatus: getEventStatusFilters(entityType, nextMonth)
      };
      setActiveFilters(newActiveFilters);
      return nextMonth;
    });
  };

  const toggleMonthView = () => {
    setShowFullMonth(prev => !prev);
  };

  const handleSwitchView = (mode: 'week' | 'month') => {
    setViewMode(mode);
    setShowFullMonth(mode === 'month');
  };

  const handleSelectedDate = (date: Date) => {
    if (entityType === 'events') {
      const auxEvents = [...events];

      if (selectedDate?.toDateString() === date.toDateString()) {
        if (!paso) {
          const filteredeventsw = (auxEvents || []).filter(
            x =>
              new Date(x.startDate).setHours(0, 0, 0, 0) <= date.setHours(0, 0, 0, 0) &&
              new Date(x.endDate).setHours(0, 0, 0, 0) >= date.setHours(0, 0, 0, 0)
          );
          setListedEvents(filteredeventsw);
          setSelectedDate(date);
          setPaso(true);
        } else {
          setSelectedDate(null);
          setListedEvents(auxEvents);
          setPaso(false);
        }
      } else {
        const filteredeventsw = (auxEvents || []).filter(
          x =>
            new Date(x.startDate).setHours(0, 0, 0, 0) <= date.setHours(0, 0, 0, 0) &&
            new Date(x.endDate).setHours(0, 0, 0, 0) >= date.setHours(0, 0, 0, 0)
        );
        setListedEvents(filteredeventsw);
        setSelectedDate(date);
        setPaso(true);
      }
    } else if (entityType === 'requests') {
      const auxRequests = [...requests];

      if (selectedDate?.toDateString() === date.toDateString()) {
        if (!paso) {
          const filteredRequestsw = (auxRequests || []).filter(
            x =>
              new Date(x.startDate).setHours(0, 0, 0, 0) <= date.setHours(0, 0, 0, 0) &&
              new Date(x.endDate).setHours(0, 0, 0, 0) >= date.setHours(0, 0, 0, 0)
          );
          setListedRequests(filteredRequestsw);
          setSelectedDate(date);
          setPaso(true);
        } else {
          setSelectedDate(null);
          setListedRequests(auxRequests);
          setPaso(false);
        }
      } else {
        const filteredRequestsw = (auxRequests || []).filter(
          x =>
            new Date(x.startDate).setHours(0, 0, 0, 0) <= date.setHours(0, 0, 0, 0) &&
            new Date(x.endDate).setHours(0, 0, 0, 0) >= date.setHours(0, 0, 0, 0)
        );
        setListedRequests(filteredRequestsw);
        setSelectedDate(date);
        setPaso(true);
      }
    }
    setCurrentDate(date);
  };

  const showControls = !showFilters && !selectedEventItem;
  const monthLabel = currentDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  const lastDay = getLastDayOfMonth(currentDate);
  const daysInMonth = lastDay.getDate();

  const load = useCallback(
    async (page: number = 1) => {
      try {
        setLoading(true);
        const activeFiltersCopy = { ...activeFilters };

        const filter = [].concat(
          ...getProperties(activeFiltersCopy)
            .filter(({ value }) => !!value)
            .map(({ value }) => value)
        );

        if (entityType === 'events') {
          const queryCopy = { ...queryRef.current, filter, take: ITEMS_PER_PAGE, skip: 0 };
          const result = await eventsStore.getAllAsync(queryCopy);
          setEvents(result.items);
          setListedEvents(result.items);
          // setTotalItems(result.count || 0);
          queryRef.current = queryCopy;
          setError(null);
        } else {
          const orderBy: OrderDefinition[] = [{ direction: 'Descending', field: nameof<RequestDto>('createdOn'), useProfile: false }];
          const queryCopy: Query = { searchQuery: '', orderBy, filter, skip: 0, take: ITEMS_PER_PAGE };
          const result = await requestsStore.getAllRequestListAsync(queryCopy);
          setRequests(result.items);
          setListedRequests(result.items);
          // setTotalItems(result.count || 0);
          queryRef.current = queryCopy;
          setError(null);
        }
      } catch (err) {
        if (err.name === 'AbortError') {
          return;
        }
        setError(err.message || 'Error');
      } finally {
        setLoading(false);
      }
    },
    [eventsStore, requestsStore, activeFilters, entityType]
  );

  const handleFilterOnChange = (type: 'instructor' | 'location', filter: any) => {
    const activeFiltersAux = { ...activeFilters };

    if (filter.instructors != null && type === 'instructor') {
      activeFiltersAux['instructors'] = filter.instructors;
    } else {
      delete activeFiltersAux['instructors'];
    }

    if (filter.eventLocation != null && type === 'location') {
      activeFiltersAux['eventLocation'] = filter.eventLocation;
    } else {
      delete activeFiltersAux['eventLocation'];
    }

    setActiveFilters(activeFiltersAux);
  };

  const initSelectedDate = useCallback(() => {
    const today = new Date();
    if (!selectedDate || selectedDate.getFullYear() !== currentDate.getFullYear() || selectedDate.getMonth() !== currentDate.getMonth()) {
      if (today.getFullYear() === currentDate.getFullYear() && today.getMonth() === currentDate.getMonth()) {
        setSelectedDate(today);
      } else {
        setSelectedDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1));
      }
      setPaso(false);
    }
  }, [selectedDate, currentDate]);

  useEffect(() => {
    const loadPage = async () => {
      await load(currentPage);
    };
    loadPage();
  }, [activeFilters, currentPage, load]);

  useEffect(() => {
    initSelectedDate();
  }, [initSelectedDate]);

  // const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
  const listRef = useRef<HTMLDivElement>(null);
  const sidebarWidthPx = 56;

  return (
    <>
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          background: '#00b2a9',
          color: 'white',
          paddingTop: '1rem',
          paddingBottom: '0.5rem',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          paddingLeft: '12px'
        }}
      >
        <h3 style={{ margin: 0 }}>{t('Calendar')}</h3>
      </div>
      <div ref={listRef} style={{ overflowY: 'auto' }} className="relative min-h-screen bg-white pb-20 flex flex-col">
        <div className="sticky top-0 z-20 bg-white" style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
          {showControls && (
            <CalendarHeader
              selectedFilterType={selectedFilterType}
              isBusy={loading || eventsStore?.state?.isBusy || requestsStore?.state?.isBusy}
              entityType={entityType}
              onToggleFilterType={type => setSelectedFilterType(type)}
              onChange={(type, filter) => handleFilterOnChange(type, filter)}
              locationList={locationList}
              setLocationList={setLocationList}
              instructorsNameList={instructorsNameList}
              setInstructorsNameList={setInstructorsNameList}
            />
          )}
          {showControls && (
            <MonthSelector
              onPrevMonth={handlePrevMonth}
              onNextMonth={handleNextMonth}
              onToggleView={toggleMonthView}
              currentMonth={monthLabel}
              viewMode={viewMode}
              onSwitchView={handleSwitchView}
              isBusy={loading || eventsStore?.state?.isBusy || requestsStore?.state?.isBusy}
            />
          )}
          {showControls && !showFullMonth && (
            <WeekdayHeader
              currentDate={currentDate}
              selectedDate={selectedDate}
              events={events}
              onSelectDate={handleSelectedDate}
              isBusy={loading || eventsStore?.state?.isBusy || requestsStore?.state?.isBusy}
              requests={requests}
              entityType={entityType}
            />
          )}
          {showControls && showFullMonth && (
            <FullMonthView
              daysInMonth={daysInMonth}
              currentDate={currentDate}
              selectedDate={selectedDate}
              onSelectDate={handleSelectedDate}
              events={events}
              isBusy={loading || eventsStore?.state?.isBusy || requestsStore?.state?.isBusy}
              requests={requests}
              entityType={entityType}
            />
          )}
        </div>
        <div
          className="flex-1 overflow-y-auto"
          style={{
            minHeight: 0
          }}
        >
          {entityType === 'events' ? (
            showFilters ? (
              <FilterScreen onClose={() => setShowFilters(false)} onSave={() => {}} />
            ) : selectedEventItem ? (
              <EventContainer
                onBack={() => setSelectedEventItem(null)}
                onClose={() => setSelectedEventItem(null)}
                selectedEvent={selectedEventItem}
                mode="ViewDetails"
                fromRequestTab={false}
                isOnlySupportDetailsEnabled={isOnlySupportDetailsEnabled}
                readOnlyForPocs={readOnlyForPocs}
              />
            ) : (
              <>
                {loading ? (
                  <MobileListSpinner />
                ) : error ? (
                  <div className="p-4 text-red-600">{error}</div>
                ) : (
                  <EventListContainer onEventClick={event => setSelectedEventItem(event)} events={listedEvents} />
                )}
                {/* {!loading && (events || []).length > 0 && (
                  <MobileListFooter
                    currentPage={currentPage}
                    onClick={setCurrentPage}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    showActionFilters={showActionFilters}
                    setShowFilters={setShowFilters}
                    entityType={'events'}
                  />
                )} */}
              </>
            )
          ) : showFilters ? (
            <FilterScreen onClose={() => setShowFilters(false)} onSave={() => {}} />
          ) : selectedRequestItem ? (
            <RequestContainer
              onBack={() => setSelectedRequestItem(null)}
              //onClose={() => setSelectedRequestItem(null)}
              selectedItem={buildRequestFormViewModel(selectedRequestItem)}
              //mode="ViewDetails"
              fromEventTab={false}
              //isOnlySupportDetailsEnabled={isOnlySupportDetailsEnabled}
              //readOnlyForPocs={readOnlyForPocs}
              history={undefined}
              location={undefined}
              match={undefined}
            />
          ) : (
            <>
              {loading ? (
                <MobileListSpinner />
              ) : error ? (
                <div className="p-4 text-red-600">{error}</div>
              ) : (
                <MobileRequestsList
                  onRequestClick={request => setSelectedRequestItem(request)}
                  requests={listedRequests}
                  fromScheduler={true}
                />
              )}
              {/* {!loading && (requests || []).length > 0 && (
                <MobileListFooter
                  currentPage={currentPage}
                  onClick={setCurrentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  showActionFilters={showActionFilters}
                  setShowFilters={setShowFilters}
                  entityType={'request'}
                />
              )} */}
            </>
          )}
        </div>
      </div>
      <div
        className="fixed bottom-0 border-t border-gray-300 bg-white px-2 py-2 shadow-lg"
        style={{
          zIndex: 100,
          minHeight: '50px',
          left: sidebarWidthPx,
          width: `calc(100% - ${sidebarWidthPx}px)`,
          display: 'flex',
          flexDirection: 'column',
          gap: '6px'
        }}
      >
        <div className="flex justify-between gap-2">
          <button
            disabled={entityType === 'events'}
            type="button"
            className={`flex-1 py-2 rounded border ${
              entityType === 'events' ? 'border-gray-200 text-gray-400' : 'border-gray-400 text-gray-800'
            } font-semibold hover:bg-gray-100 transition-all text-sm`}
            onClick={handleEntityTypeChange('events')}
            style={{ minWidth: 0 }}
          >
            {t('Events')}
          </button>
          <button
            disabled={entityType === 'requests'}
            type="button"
            className={`flex-1 py-2 rounded border ${
              entityType === 'requests' ? 'border-gray-200 text-gray-400' : 'border-gray-400 text-gray-800'
            } font-semibold hover:bg-gray-100 transition-all text-sm`}
            onClick={handleEntityTypeChange('requests')}
            style={{ minWidth: 0 }}
          >
            {t('Requests')}
          </button>
        </div>
      </div>
    </>
  );
};

export default connect(['eventsStore', EventsStore], ['requestsStore', RequestsStore])(MobileSchedulerPage);
